// The Rollout tab's pure logic: the editable draft of the `voice_engine`
// policy, its checks, the arms' bucket math and the diff against the live
// value. The server stays the authority (POST /policy/preview runs the real
// parser and Studio checks); these mirror the parser
// (app/services/client_policy/voice_engine.py) so the editor answers at once.
//
// Buckets: a user's bucket is 0–99, walked across the arms' cumulative `pct`
// in list order; whatever the arms leave is the cascade.

import type { EngineName, Policy, PolicyArm, Profile } from "../../services/voiceEngines";
import { KEY_PATTERN, KEY_RULE } from "./labels";

// Number fields stay text while editing, so a half-typed value can be
// flagged rather than silently becoming 0. A blank arm floor means the
// policy's floor.
export interface DraftArm {
  key: string;
  engine: EngineName;
  profile: string | null; // null: the engine's built-in default
  pct: number;
  min_build_ios: string;
  min_build_android: string;
}

export interface Draft {
  enabled: boolean;
  min_build_ios: string;
  min_build_android: string;
  experiment: string; // "": none yet (a v1 value, or a new policy)
  arms: DraftArm[];
}

export interface BucketRange {
  from: number;
  to: number;
}

const BUCKETS = 100;

function text(n: number | null): string {
  return n === null ? "" : String(n);
}

export function draftFromPolicy(policy: Policy): Draft {
  return {
    enabled: policy.enabled,
    min_build_ios: text(policy.min_build_ios),
    min_build_android: text(policy.min_build_android),
    experiment: policy.experiment ?? "",
    arms: policy.arms.map((a) => ({
      key: a.key,
      engine: a.engine,
      profile: a.profile,
      pct: a.pct,
      min_build_ios: text(a.min_build_ios),
      min_build_android: text(a.min_build_android),
    })),
  };
}

// A whole number ≥ 0, or null for anything else.
function build(value: string): number | null {
  const t = value.trim();
  if (!/^\d+$/.test(t)) return null;
  return Number(t);
}

// The JSON PUT /policy publishes (snake_case, as stored). Blank optional
// fields are left out rather than sent as null, so the stored value stays
// as small as the one an operator would write by hand.
export function draftToPayload(d: Draft): Record<string, unknown> {
  const out: Record<string, unknown> = { enabled: d.enabled };
  const ios = build(d.min_build_ios);
  const android = build(d.min_build_android);
  if (ios !== null) out.min_build_ios = ios;
  if (android !== null) out.min_build_android = android;
  if (d.experiment.trim()) out.experiment = d.experiment.trim();
  out.arms = d.arms.map((a) => {
    const arm: Record<string, unknown> = { key: a.key.trim(), engine: a.engine };
    if (a.profile) arm.profile = a.profile;
    arm.pct = a.pct;
    const armIos = build(a.min_build_ios);
    const armAndroid = build(a.min_build_android);
    if (armIos !== null) arm.min_build_ios = armIos;
    if (armAndroid !== null) arm.min_build_android = armAndroid;
    return arm;
  });
  return out;
}

// The draft as the server would run it (for the diff). Only meaningful when
// draftErrors() is empty.
export function draftToPolicy(d: Draft): Policy {
  return {
    enabled: d.enabled,
    min_build_ios: build(d.min_build_ios) ?? 0,
    min_build_android: build(d.min_build_android) ?? 0,
    experiment: d.experiment.trim() || null,
    arms: d.arms.map(
      (a): PolicyArm => ({
        key: a.key.trim(),
        engine: a.engine,
        profile: a.profile,
        pct: a.pct,
        min_build_ios: build(a.min_build_ios),
        min_build_android: build(a.min_build_android),
      }),
    ),
  };
}

export function pctSum(arms: { pct: number }[]): number {
  return arms.reduce((t, a) => t + a.pct, 0);
}

// What the arms leave to the cascade; negative when they ask for over 100%.
export function remainder(arms: { pct: number }[]): number {
  return BUCKETS - pctSum(arms);
}

// Each arm's buckets, inclusive, in list order; null for an arm with no
// share or one pushed past bucket 99 by the arms before it.
export function armRanges(arms: { pct: number }[]): (BucketRange | null)[] {
  let start = 0;
  return arms.map((a) => {
    const from = start;
    start += a.pct;
    if (a.pct <= 0 || from >= BUCKETS) return null;
    return { from, to: Math.min(from + a.pct, BUCKETS) - 1 };
  });
}

// The cascade's buckets after the arms; null when the arms take them all.
export function restRange(arms: { pct: number }[]): BucketRange | null {
  const sum = pctSum(arms);
  return sum < BUCKETS ? { from: Math.max(sum, 0), to: BUCKETS - 1 } : null;
}

export function rangeText(range: BucketRange | null): string {
  if (!range) return "none";
  return range.from === range.to ? String(range.from) : `${range.from}–${range.to}`;
}

// A share typed or dragged: a whole number from 0 to 100.
export function clampPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(BUCKETS, Math.round(value)));
}

// A key for a new arm: arm<n>, unused by the others.
export function newArmKey(arms: { key: string }[]): string {
  const taken = new Set(arms.map((a) => a.key.trim()));
  for (let n = arms.length + 1; ; n++) {
    if (!taken.has(`arm${n}`)) return `arm${n}`;
  }
}

// A fresh experiment key for "New experiment": voice-YYYY-MM-<letter>, the
// first letter this month that neither the current key nor an earlier
// experiment used. A new key reshuffles every user's bucket.
export function nextExperimentKey(current: string, now: Date, used: Iterable<string> = []): string {
  const prefix = `voice-${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-`;
  const taken = new Set([...used, current.trim()]);
  for (let c = "a".charCodeAt(0); c <= "z".charCodeAt(0); c++) {
    const key = prefix + String.fromCharCode(c);
    if (!taken.has(key)) return key;
  }
  return `${prefix}${now.getTime().toString(36)}`;
}

// Why the draft cannot be published, in the editor's words. Mirrors the
// parser and the Studio checks (`check_draft`); `profiles`, when given,
// also catches an arm on another engine's profile or an archived one.
export function draftErrors(d: Draft, profiles?: Profile[]): string[] {
  const errors: string[] = [];
  const floor = (label: string, value: string, required: boolean) => {
    if (!value.trim()) {
      if (required) errors.push(`Set the ${label}. An enabled policy needs both floors (0 admits every build).`);
    } else if (build(value) === null) {
      errors.push(`The ${label} must be a whole number, 0 or more.`);
    }
  };
  floor("minimum iOS build", d.min_build_ios, d.enabled);
  floor("minimum Android build", d.min_build_android, d.enabled);

  const experiment = d.experiment.trim();
  if (!experiment) {
    if (d.enabled) errors.push("Give the experiment a key. Buckets are drawn from it.");
  } else if (!KEY_PATTERN.test(experiment)) {
    errors.push(`The experiment key must be ${KEY_RULE}.`);
  }

  const keys = d.arms.map((a) => a.key.trim());
  if (keys.some((k) => !k)) errors.push("Every arm needs a key.");
  const bad = keys.filter((k) => k && !KEY_PATTERN.test(k));
  if (bad.length) errors.push(`Arm keys must be ${KEY_RULE}: ${bad.join(", ")}.`);
  const dupes = [...new Set(keys.filter((k, i) => k && keys.indexOf(k) !== i))];
  if (dupes.length) errors.push(`Arm keys must be unique: ${dupes.join(", ")}.`);

  d.arms.forEach((a) => {
    const name = a.key.trim() || "an arm";
    if (!Number.isInteger(a.pct) || a.pct < 0 || a.pct > BUCKETS) {
      errors.push(`Arm ${name}: the share must be a whole number from 0 to 100.`);
    }
    for (const [label, value] of [
      ["iOS", a.min_build_ios],
      ["Android", a.min_build_android],
    ] as const) {
      if (value.trim() && build(value) === null) {
        errors.push(`Arm ${name}: the ${label} floor must be a whole number, or blank for the policy's.`);
      }
    }
    if (a.profile && profiles) {
      const p = profiles.find((x) => x.key === a.profile);
      if (!p || p.engine !== a.engine) errors.push(`Arm ${name}: ${a.profile} is not a ${a.engine} profile.`);
      else if (p.status === "archived") errors.push(`Arm ${name}: profile ${a.profile} is archived.`);
    }
  });

  const sum = pctSum(d.arms);
  if (sum > BUCKETS) {
    errors.push(`The arms add up to ${sum}%. Keep the total at 100% or less; the rest goes to the cascade.`);
  }
  return errors;
}

// ---------------------------------------------------------------- diff

export interface DiffLine {
  field: string;
  from: string;
  to: string;
}

function armText(a: PolicyArm | undefined): string {
  if (!a) return "none";
  const floors =
    a.min_build_ios !== null || a.min_build_android !== null
      ? ` · floors iOS ${a.min_build_ios ?? "policy"}, Android ${a.min_build_android ?? "policy"}`
      : "";
  return `${a.engine} · ${a.profile ?? "built-in"} · ${a.pct}%${floors}`;
}

function show(value: unknown): string {
  if (value === null || value === undefined || value === "") return "none";
  return String(value);
}

// What publishing `draft` over `live` changes, field by field: the same rows
// as the server's 412 diff (`policy_diff`), plus the cascade's share.
export function policyDiff(live: Policy, draft: Policy): DiffLine[] {
  const rows: DiffLine[] = [];
  const add = (field: string, from: unknown, to: unknown) => {
    if (show(from) !== show(to)) rows.push({ field, from: show(from), to: show(to) });
  };
  add("enabled", live.enabled, draft.enabled);
  add("min_build_ios", live.min_build_ios, draft.min_build_ios);
  add("min_build_android", live.min_build_android, draft.min_build_android);
  add("experiment", live.experiment, draft.experiment);
  const before = new Map(live.arms.map((a) => [a.key, a]));
  const after = new Map(draft.arms.map((a) => [a.key, a]));
  const keys = [...before.keys(), ...[...after.keys()].filter((k) => !before.has(k))];
  for (const key of keys) {
    const x = before.get(key);
    const y = after.get(key);
    if (!x || !y) {
      rows.push({ field: `arm ${key}`, from: armText(x), to: armText(y) });
      continue;
    }
    add(`arm ${key} engine`, x.engine, y.engine);
    add(`arm ${key} profile`, x.profile ?? "built-in", y.profile ?? "built-in");
    add(`arm ${key} share`, `${x.pct}%`, `${y.pct}%`);
    add(`arm ${key} iOS floor`, x.min_build_ios ?? "policy", y.min_build_ios ?? "policy");
    add(`arm ${key} Android floor`, x.min_build_android ?? "policy", y.min_build_android ?? "policy");
  }
  const sharedBefore = live.arms.map((a) => a.key).filter((k) => after.has(k));
  const sharedAfter = draft.arms.map((a) => a.key).filter((k) => before.has(k));
  if (sharedBefore.join() !== sharedAfter.join()) add("arm order", sharedBefore.join(", "), sharedAfter.join(", "));
  add("cascade (rest)", `${Math.max(remainder(live.arms), 0)}%`, `${Math.max(remainder(draft.arms), 0)}%`);
  return rows;
}

// A server diff row (412 `detail.diff`) in the editor's words.
export function serverDiffLine(row: { field: string; current: unknown; draft: unknown }): DiffLine {
  const value = (v: unknown) => {
    if (v && typeof v === "object" && "engine" in v) return armText(v as PolicyArm);
    return Array.isArray(v) ? v.join(", ") : show(v);
  };
  const field = row.field.replace(/^arms\.order$/, "arm order").replace(/^arms\./, "arm ").replace(/\./g, " ");
  return { field, from: value(row.current), to: value(row.draft) };
}

// One line for a history row, e.g. "live 50% · eleven 30% · cascade 20%".
export function policySummary(p: Policy, valid = true): string {
  if (!valid) return "invalid: every call on the cascade";
  if (!p.enabled) return "off: every call on the cascade";
  const arms = p.arms.filter((a) => a.pct > 0).map((a) => `${a.key} ${a.pct}%`);
  const rest = remainder(p.arms);
  if (rest > 0) arms.push(`cascade ${rest}%`);
  return arms.join(" · ") || "no arms: every call on the cascade";
}
